import React from "react";
import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Layers, Image, Square, RectangleVertical, RectangleHorizontal, ChevronRight, ChevronLeft, AlertCircle, Check, Plus, Settings } from 'lucide-react';
import { AddCustomConventionDialog } from './AddCustomConventionDialog';
import { ManageConventionsDialog } from './ManageConventionsDialog';
import { StagedFile, GroupedAsset, GroupingResult } from '@/types/assetGrouping';
import { useAssetGrouping } from '@/hooks/useAssetGrouping';

type Step = 'distribution' | 'configuration' | 'review';

interface AssetGroupingModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  files: StagedFile[];
  onConfirm: (result: { grouped: GroupedAsset[]; singles: StagedFile[] }) => void;
}

export function AssetGroupingModal({ open, onOpenChange, files, onConfirm }: AssetGroupingModalProps) {
  const [step, setStep] = useState<Step>('distribution');
  const [distributionChoice, setDistributionChoice] = useState<'single' | 'grouped'>('grouped');
  const [groupingResult, setGroupingResult] = useState<GroupingResult>({ grouped: [], unmatched: [] });
  const [showAddConvention, setShowAddConvention] = useState(false);
  const [showManageConventions, setShowManageConventions] = useState(false);
  
  const {
    conventions,
    selectedConventionId,
    setSelectedConventionId,
    groupType,
    setGroupType,
    groupFiles,
    loading,
    refetchConventions
  } = useAssetGrouping();

  const selectedFiles = files.filter(f => f.selected);

  // Reset when modal opens
  useEffect(() => {
    if (open) {
      setStep('distribution');
      setDistributionChoice('grouped');
      setGroupingResult({ grouped: [], unmatched: [] });
    }
  }, [open]);

  // Update grouping when configuration changes
  useEffect(() => {
    if (step === 'review' && distributionChoice === 'grouped') {
      const result = groupFiles(files);
      setGroupingResult(result);
    }
  }, [step, selectedConventionId, groupType]);

  const handleNext = () => {
    if (step === 'distribution') {
      if (distributionChoice === 'single') {
        // Skip to confirm for singles
        onConfirm({ grouped: [], singles: selectedFiles });
        onOpenChange(false);
      } else {
        setStep('configuration');
      }
    } else if (step === 'configuration') {
      const result = groupFiles(files);
      setGroupingResult(result);
      setStep('review');
    }
  };

  const handleBack = () => {
    if (step === 'configuration') setStep('distribution');
    if (step === 'review') setStep('configuration');
  };

  const handleConfirm = () => {
    onConfirm({ 
      grouped: groupingResult.grouped, 
      singles: groupingResult.unmatched 
    });
    onOpenChange(false);
  };

  const renderDistributionStep = () => (
    <div className="space-y-6 py-4">
      <div className="text-center">
        <p className="text-muted-foreground">
          How would you like to organize your {selectedFiles.length} selected files?
        </p>
      </div>

      <RadioGroup
        value={distributionChoice}
        onValueChange={(v) => setDistributionChoice(v as 'single' | 'grouped')}
        className="grid gap-4"
      >
        <div className="relative">
          <RadioGroupItem value="single" id="single" className="peer sr-only" />
          <Label
            htmlFor="single"
            className="flex flex-col items-center justify-between rounded-lg border-2 border-muted bg-popover p-4 hover:bg-accent hover:text-accent-foreground peer-data-[state=checked]:border-primary [&:has([data-state=checked])]:border-primary cursor-pointer"
          >
            <Image className="h-8 w-8 mb-3 text-muted-foreground" />
            <span className="font-semibold">Upload as Singles</span>
            <span className="text-sm text-muted-foreground text-center mt-1">
              Each file will be uploaded individually without grouping
            </span>
          </Label>
        </div>

        <div className="relative">
          <RadioGroupItem value="grouped" id="grouped" className="peer sr-only" />
          <Label
            htmlFor="grouped"
            className="flex flex-col items-center justify-between rounded-lg border-2 border-muted bg-popover p-4 hover:bg-accent hover:text-accent-foreground peer-data-[state=checked]:border-primary [&:has([data-state=checked])]:border-primary cursor-pointer"
          >
            <Layers className="h-8 w-8 mb-3 text-muted-foreground" />
            <span className="font-semibold">Group by Naming Convention</span>
            <span className="text-sm text-muted-foreground text-center mt-1">
              Automatically pair or triple files based on filename patterns (e.g., video_1x1.mp4 + video_9x16.mp4)
            </span>
          </Label>
        </div>
      </RadioGroup>
    </div>
  );

  const renderConfigurationStep = () => (
    <div className="space-y-6 py-4">
      <div className="space-y-3">
        <Label className="text-base font-medium">Group Type</Label>
        <RadioGroup
          value={groupType}
          onValueChange={(v) => setGroupType(v as 'pair' | 'triple')}
          className="grid grid-cols-2 gap-4"
        >
          <div className="relative">
            <RadioGroupItem value="pair" id="pair" className="peer sr-only" />
            <Label
              htmlFor="pair"
              className="flex flex-col items-center justify-center rounded-lg border-2 border-muted bg-popover p-4 hover:bg-accent hover:text-accent-foreground peer-data-[state=checked]:border-primary [&:has([data-state=checked])]:border-primary cursor-pointer"
            >
              <div className="flex items-center gap-2 mb-2">
                <Square className="h-5 w-5" />
                <RectangleVertical className="h-5 w-5" />
              </div>
              <span className="font-medium text-sm">Pairs (Square + Vertical)</span>
            </Label>
          </div>

          <div className="relative">
            <RadioGroupItem value="triple" id="triple" className="peer sr-only" />
            <Label
              htmlFor="triple"
              className="flex flex-col items-center justify-center rounded-lg border-2 border-muted bg-popover p-4 hover:bg-accent hover:text-accent-foreground peer-data-[state=checked]:border-primary [&:has([data-state=checked])]:border-primary cursor-pointer"
            >
              <div className="flex items-center gap-2 mb-2">
                <Square className="h-5 w-5" />
                <RectangleVertical className="h-5 w-5" />
                <RectangleHorizontal className="h-5 w-5" />
              </div>
              <span className="font-medium text-sm">Triples (Square + Vertical + Landscape)</span>
            </Label>
          </div>
        </RadioGroup>
      </div>

      <div className="space-y-3">
        <Label className="text-base font-medium">Naming Convention</Label>
        <Select
          value={selectedConventionId || ''}
          onValueChange={setSelectedConventionId}
        >
          <SelectTrigger>
            <SelectValue placeholder="Select a naming convention" />
          </SelectTrigger>
          <SelectContent>
            {conventions.map((conv) => (
              <SelectItem key={conv.id} value={conv.id}>
                <div className="flex flex-col">
                  <span className="font-medium">{conv.label}</span>
                  <span className="text-xs text-muted-foreground">
                    {conv.square_suffix} / {conv.vertical_suffix}
                    {conv.landscape_suffix && ` / ${conv.landscape_suffix}`}
                  </span>
                </div>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <div className="flex gap-2 mt-2">
          <Button 
            variant="outline" 
            size="sm" 
            onClick={() => setShowAddConvention(true)}
          >
            <Plus className="h-4 w-4 mr-1" />
            Add Custom Convention
          </Button>
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={() => setShowManageConventions(true)}
          >
            <Settings className="h-4 w-4 mr-1" />
            Manage
          </Button>
        </div>
        <p className="text-xs text-muted-foreground">
          Files will be matched by removing the suffix to find the base name
        </p>
      </div>
    </div>
  );

  const renderReviewStep = () => (
    <div className="space-y-6 py-4 max-h-[60vh] overflow-y-auto">
      {groupingResult.grouped.length > 0 && (
        <div className="space-y-4">
          <div className="flex items-center gap-2">
            <Check className="h-5 w-5 text-green-500" />
            <Badge variant="secondary" className="text-sm">
              {groupingResult.grouped.length} {groupType === 'pair' ? 'Pairs' : 'Triples'} Found
            </Badge>
          </div>

          <div className="grid gap-3">
            {groupingResult.grouped.map((group, idx) => (
              <Card key={idx} className="p-3">
                <div className="flex items-center gap-2 mb-2">
                  <Badge variant="outline">{group.group_type}</Badge>
                  <span className="font-medium text-sm truncate">{group.group_name}</span>
                </div>
                <div className="flex gap-2 overflow-x-auto">
                  {group.assets.map((asset, i) => (
                    <div key={i} className="flex-shrink-0">
                      <div className="w-16 h-16 bg-muted rounded overflow-hidden">
                        {asset.file.type === 'image' ? (
                          <img
                            src={asset.file.preview_url}
                            alt={asset.file.name}
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <video
                            src={asset.file.preview_url}
                            className="w-full h-full object-cover"
                            muted
                          />
                        )}
                      </div>
                      <Badge variant="secondary" className="mt-1 text-xs w-full justify-center">
                        {asset.role}
                      </Badge>
                    </div>
                  ))}
                </div>
              </Card>
            ))}
          </div>
        </div>
      )}

      {groupingResult.unmatched.length > 0 && (
        <div className="space-y-3">
          <div className="flex items-center gap-2">
            <AlertCircle className="h-5 w-5 text-yellow-500" />
            <span className="text-sm text-muted-foreground">
              {groupingResult.unmatched.length} file(s) could not be matched and will be uploaded as singles
            </span>
          </div>

          <div className="flex flex-wrap gap-2">
            {groupingResult.unmatched.map((file) => (
              <Badge key={file.id} variant="outline">
                {file.name}
              </Badge>
            ))}
          </div>
        </div>
      )}

      {groupingResult.grouped.length === 0 && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            No matching groups found with the selected naming convention. Try a different convention or upload files as singles.
          </AlertDescription>
        </Alert>
      )}
    </div>
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>
            {step === 'distribution' && 'Organize Your Creatives'}
            {step === 'configuration' && 'Configure Grouping'}
            {step === 'review' && 'Review Groups'}
          </DialogTitle>
        </DialogHeader>

        {step === 'distribution' && renderDistributionStep()}
        {step === 'configuration' && renderConfigurationStep()}
        {step === 'review' && renderReviewStep()}

        <DialogFooter className="flex justify-between sm:justify-between">
          <div>
            {step !== 'distribution' && (
              <Button variant="outline" onClick={handleBack}>
                <ChevronLeft className="h-4 w-4 mr-1" />
                Back
              </Button>
            )}
          </div>

          <div className="flex gap-2">
            <Button variant="ghost" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            {step === 'review' ? (
              <Button onClick={handleConfirm} disabled={groupingResult.grouped.length === 0 && groupingResult.unmatched.length === 0}>
                Confirm & Upload
              </Button>
            ) : (
              <Button onClick={handleNext}>
                {distributionChoice === 'single' && step === 'distribution' ? 'Upload as Singles' : 'Next'}
                <ChevronRight className="h-4 w-4 ml-1" />
              </Button>
            )}
          </div>
        </DialogFooter>
      </DialogContent>

      <AddCustomConventionDialog
        open={showAddConvention}
        onOpenChange={setShowAddConvention}
        onSuccess={() => {
          refetchConventions();
        }}
      />

      <ManageConventionsDialog
        open={showManageConventions}
        onOpenChange={setShowManageConventions}
        onConventionDeleted={() => {
          refetchConventions();
        }}
      />
    </Dialog>
  );
}
